import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonGroup,
  color,
  DropZone,
  DropZoneV2,
  fileRejectionsType,
  Icon,
  Modal,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';

import { coreApi } from '@/api';
import { onMobile } from '@/styles/responsive';
import { MarginBottom10, MarginBottom16, MarginZero, TextAlignCenter } from '@/styles';
import { dropZoneMessage } from '@/messageUtils';
import { toast, makeFormData, checkFileSize, maxDefaultFileSize, ModalContent } from '@/utils';
import {
  useDidUpdateEffect,
  useModalState,
  usePostRequest,
  useBrowserDownload,
  useRequest,
  useFileUpload,
  useAiCreditCheck
} from '@/hooks';
import type { SelectedButtonType } from '@/pages/theme/types';
import { UploadLoadingSpinner } from './Uploader';
import AiCreateButton from '@/pages/ai/components/AiCreateButton';
import CreateAiBgmModalTemplate from '@/pages/ai/components/CreateAiBgmModalTemplate';
import AiLoadingBox from '@/pages/ai/components/AiLoadingBox';
import AiUsageNotice from '@/components/AiUsageNotice';
import { ClickEventName, PageViewEventName, track } from '@/track';

interface DefaultValueProps {
  propsDefaultBgmFileName?: string;
  propsDefaultBgmUrl?: string;
  propsDefaultIsFadeIn?: boolean;
  propsDefaultIsFadeOut?: boolean;
}

interface BGMUploaderProps extends DefaultValueProps {
  bgmUseForType?: string;
  onChangeUploadedBGM: (bgm?: FileResponseModel) => void;
  onChangeLoading?: (isLoading: boolean) => void;
  onChangeFadeButton: (selected: SelectedButtonType) => void;
  /**
   * default: false
   */
  isHideFadeButton?: boolean;
}

export default function BGMUploader({
  propsDefaultBgmFileName,
  bgmUseForType,
  onChangeUploadedBGM,
  onChangeLoading,
  onChangeFadeButton,
  propsDefaultBgmUrl,
  propsDefaultIsFadeIn = true,
  propsDefaultIsFadeOut = true,
  isHideFadeButton = false
}: BGMUploaderProps) {
  const { appId, projectId } = useParams<AppParam>();
  const projectName = sessionStorage.getItem('projectName');

  const { modal, openModal, closeModal } = useModalState();
  const [acceptedFilesLength, setAcceptedFilesLength] = useState(0);
  const [rejectedFiles, setRejectedFiles] = useState<fileRejectionsType>([]);

  const [bgmFile, setBgmFile] = useState<FileResponseModel>();
  const [defaultBgmUrl, setDefaultBgmUrl] = useState(propsDefaultBgmUrl);
  const [isBgmFadeIn, setIsBgmFadeIn] = useState(propsDefaultIsFadeIn);
  const [isBgmFadeOut, setIsBgmFadeOut] = useState(propsDefaultIsFadeOut);
  const [isLoading, setIsLoading] = useState(false);
  const [alertContent, setAlertContent] = useState<ModalContent>();
  const isAlertOpen = alertContent !== undefined;
  const missionSessionData = JSON.parse(sessionStorage.getItem('missionSessionData') ?? '{}');
  const questSessionData = JSON.parse(sessionStorage.getItem('questSessionData') ?? '{}');

  const { checkCreditAndExecute, modal: aiCreditModal } = useAiCreditCheck();

  const handleDeleteBGM = () => {
    if (typeof defaultBgmUrl !== 'undefined') {
      setDefaultBgmUrl(undefined);
      onChangeUploadedBGM(undefined);
    }
    setBgmFile(undefined);
    // BGM 파일 업로드 -> 바로 삭제 -> 저장 클릭 시 BGM URL이 들어간 상태로 전송되는 문제가 있어 주석 처리했습니다.
    // 어떤 의도로 작성한 코드인지 몰라 일단 주석 처리해두겠습니다.
    // onChangeUploadedBGM(bgmFile);
  };

  function closeAlert() {
    setAlertContent(undefined);
  }

  const onChangeFile = async (files: File[]) => {
    if (files.length > 1) {
      toast({ message: '하나의 파일만 올릴 수 있어요', type: 'error' });
      return;
    }

    const isOverSize = checkFileSize({ file: files[0] });
    if (isOverSize) {
      openModal({
        title: '더 작은 이미지를 올려주세요',
        children: (
          <>
            <Icon
              cssStyle={MarginBottom10}
              icon="times_circle_regular"
              size="100px"
              color={color.interactive_critical_default}
            />
            {`최대 ${maxDefaultFileSize / 1024 / 1024}MB까지 업로드 할 수 있어요`}
          </>
        ),
        primaryAction: { content: '확인', onAction: closeModal }
      });
      return;
    }

    setIsLoading(true);

    setAcceptedFilesLength(files.length);

    try {
      const { data } = await coreApi.post<FileResponseModel>(
        `/apps/${appId}/projects/${projectId}/files`,
        makeFormData({ file: files[0], dir: '.musicfiles' })
      );
      setIsLoading(false);
      setBgmFile(data);
      onChangeUploadedBGM(data);
      onChangeFadeButton({ isBgmFadeIn: true, isBgmFadeOut: true });
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (rejectedFiles.length) {
      openModal({
        title: `${
          acceptedFilesLength > 0
            ? `형식에 맞지 않는 파일이 ${rejectedFiles.length}개 있어요`
            : '형식에 맞는 파일을 업로드해주세요'
        }`,
        cssStyle: TextAlignCenter,
        children: (
          <>
            <Icon
              color={color.interactive_critical_default}
              icon="times_circle_regular"
              size="100px"
            />
            {dropZoneMessage.mp3}
          </>
        ),
        primaryAction: { content: '확인', onAction: closeModal }
      });
    }
  }, [rejectedFiles]);

  useEffect(() => {
    if (typeof onChangeLoading === 'undefined') return;
    onChangeLoading(isLoading);
  }, [isLoading]);

  useDidUpdateEffect(() => {
    // 초기 props 값과 동일하면 콜백을 호출하지 않음 (isDirty가 잘못 설정되는 것 방지)
    if (isBgmFadeIn === propsDefaultIsFadeIn && isBgmFadeOut === propsDefaultIsFadeOut) {
      return;
    }
    onChangeFadeButton({ isBgmFadeIn, isBgmFadeOut });
  }, [isBgmFadeIn, isBgmFadeOut]);

  // AI PROJECT AREA - hooks를 early return 이전에 배치
  const aiBgmDataRef = useRef<string | null>(null);
  const { data: aiProductData } =
    useRequest<CommonResponseModel<AiProductResponseModel[]>>(`/api/products/ai`);
  const {
    trigger: generateAiContent,
    isLoading: isGeneratingAiContent,
    error: generateAiContentError
  } = usePostRequest<CommonResponseModel<GenerateResourceResponseModel>>(
    `/apps/${appId}/ai/generations`
  );

  const { mutate: creditRefetch } = useRequest<CommonResponseModel<AiProductResponseModel[]>>(
    `/apps/${appId}/credit`
  );
  const { download: downloadFile } = useBrowserDownload();

  // useFileUpload hook for button file upload
  const buttonFileUpload = useFileUpload({
    type: 'bgm',
    multiple: false,
    maxFileSize: maxDefaultFileSize,
    onSuccess: (files) => {
      if (files.length > 0) {
        setBgmFile(files[0]);
        onChangeUploadedBGM(files[0]);
      }
    },
    onError: () => {
      setAlertContent({
        title: '업로드 실패',
        message: '파일 업로드 중 오류가 발생했어요. 다시 시도해 주세요.'
      });
    }
  });

  // 모든 hooks 이후에 조건부 return 배치
  if (isLoading || buttonFileUpload.isLoading) return <UploadLoadingSpinner isMultiple={false} />;

  // AI BGM 생성 액션 함수
  const handleAiBgmGenerate = async () => {
    // mixpanel event data test
    console.log('mixpanel event data', {
      clickEventName: ClickEventName.click_editgame_modal_aigenerate_bgm,
      params: {
        channel_id: appId,
        project_name: projectName,
        project_id: projectId,
        prompt: aiBgmDataRef.current ?? ''
      }
    });
    track.onClick({
      clickEventName: ClickEventName.click_editgame_modal_aigenerate_bgm,
      params: {
        channel_id: appId,
        project_name: projectName,
        project_id: projectId,
        prompt: aiBgmDataRef.current ?? ''
      }
    });
    try {
      closeModal();
      const result = await generateAiContent({
        generateType: 'Bgm',
        input: {
          prompt: aiBgmDataRef.current ?? '',
          duration: 30000
        },
        projectId
      });

      if (result?.data?.fileId && result?.data?.url) {
        const data = {
          id: result.data.fileId,
          isDirectory: false,
          fileName: result.data.fileId,
          url: result.data.url,
          createdAt: new Date().toISOString(),
          originalName: result.data.fileId
        } as FileResponseModel;

        setBgmFile(data);
        onChangeUploadedBGM(data);
        creditRefetch();
        try {
          await downloadFile(result.data.url, `ai-bgm-${result.data.fileId}`);
        } catch {
          setAlertContent({
            title: '다운로드 실패',
            message: '브라우저에서 파일 저장을 시작하지 못했어요. 다운로드 권한을 확인해 주세요.'
          });
        }
      } else {
        setAlertContent({
          title: 'AI 생성 실패',
          message: '오디오 정보를 가져오지 못했어요. 잠시 후 다시 시도해 주세요.'
        });
      }
    } catch (err) {
      const errorTitle = (err as AxiosError<{ errorTitle: string }>)?.response?.data.errorTitle;
      if (errorTitle === 'NotEnoughCredit') {
        toast({
          type: 'error',
          message: '크레딧이 모두 소진됐어요. 내일 다시 이용해 주세요.'
        });
      } else {
        toast({
          type: 'error',
          message: '요청 처리 중 오류가 발생했어요. 잠시 후 다시 시도해 주세요.'
        });
      }
    }
  };

  const openCreateAiBgmModal = () => {
    console.log('mixpanel event data', {
      clickEventName: PageViewEventName.view_editgame_modal_aigenerate_bgm,
      params: {
        channel_id: appId,
        project_name: projectName,
        project_id: projectId
      }
    });
    track.onPageViewMixpanel(PageViewEventName.view_editgame_modal_aigenerate_bgm, {
      channel_id: appId,
      project_name: projectName,
      project_id: projectId
    });
    openModal({
      title: 'AI로 BGM 만들기',
      size: 'large',
      hasFooter: false,
      isBottomRadius: true,
      children: (
        <div css={ModalContentContainer}>
          {/* Content 영역 */}
          <div css={ModalContentArea}>
            <CreateAiBgmModalTemplate
              onDescriptionChange={(data) => {
                aiBgmDataRef.current = data;
              }}
            />
          </div>

          {/* Divider */}
          <div css={ModalDivider} />

          {/* Custom Footer */}
          <div css={ModalFooterContainer}>
            <AiUsageNotice />

            {/* 오른쪽 버튼 영역 */}
            <AiCreateButton
              creditValue={
                aiProductData?.data.find((product) => product.generateType === 'Bgm')
                  ?.creditPrice ?? 10
              }
              width="146px"
              height="40px"
              onClick={handleAiBgmGenerate}
            />
          </div>
        </div>
      )
    });
  };

  const convertBgmUseForTypeToMixpanelEventName = (bgmUseForType: string) => {
    switch (bgmUseForType) {
      case 'theme-bgm':
        return ClickEventName.click_editgame_theme_bgm_aigenerate;
      case 'audio-player':
        return ClickEventName.click_editgame_mission_quest_audioplayer_aigenerate;
      case 'change-bgm':
        return ClickEventName.click_editgame_mission_quest_gamebgm_aigenerate;
      default:
        return ClickEventName.click_editgame_theme_bgm_aigenerate;
    }
  };

  return (
    <>
      <Modal {...aiCreditModal} />
      <Modal {...modal} />
      <Modal
        open={isAlertOpen}
        title={alertContent?.title}
        onClose={closeAlert}
        primaryAction={{ content: '확인', type: 'primary', onAction: closeAlert }}
        cssStyle={TextAlignCenter}
      >
        {alertContent?.message}
      </Modal>
      {!bgmFile && !defaultBgmUrl && !isGeneratingAiContent && (
        // <DropZone
        //   accepts={['.mp3']}
        //   ratio="fullWidth"
        //   cssStyle={FullWidth}
        //   onChangeFile={onChangeFile}
        //   notAcceptFiles={setRejectedFiles}
        // />
        <DropZoneV2
          accepts={['.mp3']}
          ratio="fullWidth"
          cssStyle={FullWidth}
          onChangeFile={onChangeFile}
          onRejectFiles={setRejectedFiles}
        />
      )}
      {!bgmFile && !defaultBgmUrl && isGeneratingAiContent && (
        <div css={AiLoadingBoxContainer}>
          <AiLoadingBox />
        </div>
      )}
      {(bgmFile || defaultBgmUrl) && (
        <figure css={MarginZero}>
          <Typography as="figcaption" type="button" cssStyle={MarginBottom16}>
            {((bgmFile && !bgmFile.isDirectory && bgmFile.originalName) ||
              propsDefaultBgmFileName) ??
              ''}
          </Typography>
          <audio css={[PlayerStyle, MarginBottom16]} controls src={bgmFile?.url ?? defaultBgmUrl}>
            <track kind="captions" />
            <code>audio</code>를 지원하지 않는 브라우저예요.
          </audio>
          <div css={ButtonStyle}>
            {!isHideFadeButton && (
              <ButtonGroup segmented>
                <Button
                  type={isBgmFadeIn ? 'primary' : 'outlinePrimary'}
                  pressed={isBgmFadeIn}
                  onClick={() => setIsBgmFadeIn((prev) => !prev)}
                >
                  페이드 인
                </Button>
                <Button
                  type={isBgmFadeOut ? 'primary' : 'outlinePrimary'}
                  pressed={isBgmFadeOut}
                  onClick={() => setIsBgmFadeOut((prev) => !prev)}
                >
                  페이드 아웃
                </Button>
              </ButtonGroup>
            )}
            <Button
              type="destructive"
              icon="times_light"
              cssStyle={DeleteButtonStyle}
              onClick={handleDeleteBGM}
            >
              삭제
            </Button>
          </div>
        </figure>
      )}
      {!bgmFile && !defaultBgmUrl && !isGeneratingAiContent && (
        <div css={UploadBottomSection}>
          <Typography type="body" textColor="initial">
            MP3 &#40;10MB 이하&#41;
          </Typography>
          <div css={AiCreateButtonContainerStyle}>
            <Button
              type="basic"
              size="small"
              icon="upload_solid"
              disabled={buttonFileUpload.isLoading}
              cssStyle={css`
                border-radius: ${spacing.common.small};
              `}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.mp3';
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement;
                  if (target.files && target.files.length > 0) {
                    buttonFileUpload.uploadFiles(Array.from(target.files));
                  }
                };
                input.click();
              }}
            >
              {buttonFileUpload.isLoading ? '업로드 중...' : '업로드'}
            </Button>
            <AiCreateButton
              creditValue={
                aiProductData?.data.find((product) => product.generateType === 'Bgm')
                  ?.creditPrice ?? 10
              }
              width="146px"
              height="32px"
              onClick={() => {
                checkCreditAndExecute(() => {
                  openCreateAiBgmModal();

                  if (
                    convertBgmUseForTypeToMixpanelEventName(bgmUseForType ?? '') ===
                    'click_editgame_theme_bgm_aigenerate'
                  ) {
                    // mixpanel event data test
                    console.log('mixpanel event data', {
                      clickEventName: convertBgmUseForTypeToMixpanelEventName(bgmUseForType ?? ''),
                      params: {
                        channel_id: appId,
                        project_name: projectName,
                        project_id: projectId
                      }
                    });

                    track.onClick({
                      clickEventName: convertBgmUseForTypeToMixpanelEventName(bgmUseForType ?? ''),
                      params: {
                        channel_id: appId,
                        project_name: projectName,
                        project_id: projectId
                      }
                    });
                  }
                  if (
                    convertBgmUseForTypeToMixpanelEventName(bgmUseForType ?? '') ===
                      'click_editgame_mission_quest_audioplayer_aigenerate' ||
                    convertBgmUseForTypeToMixpanelEventName(bgmUseForType ?? '') ===
                      'click_editgame_mission_quest_gamebgm_aigenerate'
                  ) {
                    // mixpanel event data test
                    console.log('mixpanel event data', {
                      clickEventName: convertBgmUseForTypeToMixpanelEventName(bgmUseForType ?? ''),
                      params: {
                        channel_id: appId,
                        project_name: projectName,
                        project_id: projectId,
                        mission_id: missionSessionData?.id ?? '',
                        mission_name: missionSessionData?.name ?? '',
                        quest_id: questSessionData?.id ?? '',
                        quest_name: questSessionData?.name ?? ''
                      }
                    });

                    track.onClick({
                      clickEventName: convertBgmUseForTypeToMixpanelEventName(bgmUseForType ?? ''),
                      params: {
                        channel_id: appId,
                        project_name: projectName,
                        project_id: projectId,
                        mission_id: missionSessionData?.id ?? '',
                        mission_name: missionSessionData?.name ?? '',
                        quest_id: questSessionData?.id ?? '',
                        quest_name: questSessionData?.name ?? ''
                      }
                    });
                  }
                }, aiProductData?.data.find((product) => product.generateType === 'Bgm')?.creditPrice ?? 10);
              }}
            />
          </div>
          {/* <Typography type="caption" textColor="subdued">
            *AI가 &#39;미션&#39;의 내용을 참고하여 파일을 만들어요.
          </Typography> */}
        </div>
      )}
    </>
  );
}

const PlayerStyle = css`
  width: 100%;
  height: 52px;

  ${onMobile} {
    width: 300px;
  }
`;

const ButtonStyle = css`
  display: flex;

  ${onMobile} {
    flex-direction: column;
  }
`;

const DeleteButtonStyle = css`
  margin-left: auto;

  ${onMobile} {
    margin-top: ${spacing.common.small};
    margin-left: 0px;
    margin-right: auto;
  }
`;

const FullWidth = css`
  .DragDrop-label {
    width: 100%;
  }
`;

const AiCreateButtonContainerStyle = css`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: start;
  gap: ${spacing.common.small};
`;

const UploadBottomSection = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.small};
  margin-top: ${spacing.common.small};
`;

const AiLoadingBoxContainer = css`
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// AI BGM Modal Styles
const ModalContentContainer = css`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ModalContentArea = css`
  flex: 1;
  overflow: auto;
`;

const ModalDivider = css`
  width: calc(100% + 48px);
  height: 1px;
  margin-top: 24px;
  margin-left: -24px;
  background-color: #e4e5e7;
`;

const ModalFooterContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: -24px;
  padding: 16px 0px;
  background-color: #ffffff;
  gap: 16px;
  flex-wrap: wrap;
`;
